/* eslint-disable jsx-a11y/alt-text */
import { Image, Text, View } from '@react-pdf/renderer';
import imageUrlBuilder from '@sanity/image-url';

import { Instructor } from '~/features/program/models/program-content';
import { sanityClient } from '~/libs/sanity';

import { createPdfStyles } from '../utils/create-pdf-styles';

import { styles as sharedStyles } from './syllabus-styles';

type SyllabusInstructorsProps = {
  instructors: Instructor[];
};

const styles = createPdfStyles({
  instructorsGrid: {
    ...sharedStyles.topLevelStack,
    justifyContent: 'flex-start',
    gap: '12px',
  },
  instructorBox: {
    backgroundColor: 'blue.700',
    borderRadius: 12,
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '12px',
    flexGrow: 0,
    flexBasis: '48%',
  },
  instructorImage: {
    width: '56px',
    height: '56px',
    borderRadius: 28,
    objectFit: 'cover',
    border: '1px solid black',
  },
  instructorImageContainer: {
    width: '64px',
    height: '64px',
    borderRadius: 32,
    backgroundColor: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  instructorInfo: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '8px',
  },
  instructorNameJobBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    maxWidth: '230px',
  },
  instructorName: {
    fontSize: '17px',
    fontWeight: 400,
    color: '#ffffff',
    marginBottom: 4,
    fontFamily: 'PolySans',
  },
  instructorTitle: {
    fontSize: '12px',
    fontFamily: 'Open Sans',
    fontWeight: 400,
    color: 'accent.lime',
  },
  instructorBio: {
    fontSize: '10px',
    lineHeight: 1.5,
    fontWeight: 400,
    fontFamily: 'Open Sans',
    color: '#ffffff',
  },
});

function InstructorComponent({ instructor }: { instructor: Instructor }) {
  const builder = imageUrlBuilder(sanityClient);
  const imageUrl = instructor.image?.asset.url;
  const processedImageUrl = imageUrl
    ? builder.image(imageUrl).fit('clip').width(96).crop('center').auto('format').url()
    : null;
  return (
    <View key={instructor._id} style={styles.instructorBox} wrap={false}>
      <View style={styles.instructorInfo}>
        {processedImageUrl && (
          <View style={styles.instructorImageContainer}>
            <Image src={processedImageUrl} style={styles.instructorImage} />
          </View>
        )}
        <View style={styles.instructorNameJobBox}>
          <Text style={styles.instructorName}>{instructor.name}</Text>
          {instructor.jobTitle && <Text style={styles.instructorTitle}>{instructor.jobTitle}</Text>}
        </View>
      </View>
      <View
        style={{
          borderTop: '0.5px solid #D8DBE9',
          width: '100%',
          opacity: '0.2',
        }}
      />
      <View>{instructor.bio && <Text style={styles.instructorBio}>{instructor.bio}</Text>}</View>
    </View>
  );
}

export function SyllabusInstructors({ instructors }: SyllabusInstructorsProps) {
  if (!instructors || instructors.length === 0) {
    return null;
  }

  return (
    <View style={sharedStyles.section}>
      <Text
        style={{
          ...sharedStyles.sectionTitle,
          marginBottom: '16px',
        }}
      >
        Meet Your Instructors
      </Text>
      <View style={styles.instructorsGrid}>
        {instructors.map((instructor) => (
          <InstructorComponent key={instructor._id} instructor={instructor} />
        ))}
      </View>
    </View>
  );
}
